import { AbstractControl, AsyncValidatorFn, FormArray, FormControl, FormGroup, ValidatorFn, Validators } from "@angular/forms";
import { ApiDefaultResponse, InvalidControlInreface } from "../interfaces/global.interface";
import { PromptMessage } from "./promp-message.class";
import { HttpErrorResponse } from "@angular/common/http";
import { UserAccess } from "./user-access.class";
import { Observer } from "rxjs";
import { ActivatedRoute } from "@angular/router";
import { environment } from "src/environments/environment";
import { CustomValidator } from "../validators/custom-validator";
import { AccTableComponent } from "../../custom-components/acc-table/acc-table.component";
import { WaitHandler } from './wait-handler.class';
import { SplineChartOptions } from "../../custom-components/acc-splinechart/acc-splinechart.component";
import { PieChartOptions } from "../../custom-components/acc-piechart/acc-piechart.component";
import { BarChartOptions } from "../../custom-components/acc-barchart/acc-barchart.component";

export class Helper {

  public static readonly env = environment;

  public static newWaitHandler(debugMode: boolean = false) {
    return new WaitHandler(debugMode);
  }

  public static newPromptMessage() {
    return new PromptMessage();
  }

  public static readonly classes = {
    activeTabClass: 'text-red-600 border-red-600 bg-zinc-100 active',
    nonActiveTabClass: 'border-transparent hover:text-gray-600 hover:border-gray-300',
  }

  public static accTablePrint(table: AccTableComponent) {
    table.printTable();
  }

  public static getIdOnSearchParam(aRoute?: ActivatedRoute | null, key: string = 'id'): string {
    if (!aRoute) return '';
    return aRoute.snapshot.paramMap.get(key) ?? '';
  }

  public static readonly url = {
    toParams: (baseURL: string, obj: any): string => {
      const body: Record<string, string> = {};
      Object.keys(obj).forEach(key => {
        const value = obj[key];
        if (value === null || value === undefined) return;
        if (value === '') return;
        else body[key] = String(value);
      });
      const params = new URLSearchParams(body);
      return `${baseURL}?${params.toString()}`;
    },
    getIdOnPath: (aRoute?: ActivatedRoute | null, key: string = 'id'): string => {
      if (!aRoute) return '<not_found>';
      return aRoute.snapshot.paramMap.get(key) ?? '';
    }
  }

  public static readonly api = {
    handleStatusResponse: (parameters: {
      prompt?: PromptMessage,
      successMessage?: string,
      onSuccess: (data: any) => void,
    }): Partial<Observer<any>> | undefined => {
      const { prompt, successMessage, onSuccess } = parameters;
      return {
        complete: () => { },
        next: (data) => {
          if (data?.status == 1) {
            prompt?.set(successMessage ?? 'Success', 'alert alert-success mb-4');
            onSuccess(data);
          } else {
            Helper.handleError(data?.message, prompt);
          }
        },
        error: (error: HttpErrorResponse) => {
          Helper.handleError(error, prompt);
        }
      } as Partial<Observer<any>>
    }
  }

  public static readonly errorMessage = {
    requiredFields: `* Please fill out required fields.`,
    apiError: '* Error occured!',
    apiErrorMessage: (errMsg: string) => `* Error message: ${errMsg}`,
  }

  public static readonly users = {

    /**
     * Convert `UID` number into `formated type`.
     * 
     * Ex: `1` => `AGF-00001`
     */
    formatUID: (userID?: number | null) => {
      if (!userID) return '';
      return `AGF-${String(userID).padStart(5, '0')}`;
    },

    toIDNumber: (formatedID?: string | null) => {
      return Helper.string.formatId.toNumber(formatedID);
    },

    accessModules: UserAccess.modules,
    accessList: UserAccess.accessList,

  }

  public static readonly file = {

    /**
     * Convert image file into base64
     * @param file - the image file from the input:file.
     */
    imgFileToBase64: (file?: File | null): Promise<string | null> => {
      return new Promise((resolve, reject) => {
        if (!file || !file.type.startsWith('image/')) {
          reject(new Error('Invalid file type. Please upload an image.'));
          return;
        }

        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event: ProgressEvent<FileReader>) => {
          if (event.target && event.target.result) {
            resolve(event.target.result as string);
          } else {
            reject(new Error('Failed to read image file.'));
          }
        };
        reader.onerror = (error) => {
          reject(new Error('Error reading image file: ' + error));
        };
      });
    },

    getExtension: (file?: File | null): string => {
      if (!file) return '';
      return file.name.split(".")[1];
    }
  }

  public static readonly array = {

    sortByPropertyName: <T>(value?: T[] | null, propertyName?: keyof T | null, ascending = true) => {
      if (!value) return [];
      if (!propertyName) return [];
      const final = value.sort((a, b) => {
        const first = a[propertyName];
        const second = b[propertyName];

        if (first == null && second == null) return 0;
        if (first == null) return -1;
        if (second == null) return 1;

        return Helper.string.trimLowerCase(String(first)).localeCompare(Helper.string.trimLowerCase(String(second)));
      })

      return ascending ? final : final.reverse();
    },

    sort: <T>(value?: T[] | null, custom?: (() => number) | null) => {
      if (!value) return [];
      if (!custom) return value.sort();
      return value.sort(custom);
    },

    compareArrays: (arrays: any[], manipulate: (selected: any[]) => any) => {
      return manipulate([...arrays]);
    },

    makeArrayOfNumber: (start: number, end: number) => Array(end - start).fill(0).map((_, i) => start + i + 1),

    arr1InArr2: (arr1?: any[] | null, arr2?: any[]) => {
      if (!arr1) return false;
      if (!arr2) return false;

      const set1 = new Set(arr1);

      return arr2.some(element => set1.has(element));
    },

    hasDuplicateValues: (arr?: any[] | null) => {
      if (!arr) return false;

      const uniqueValues = new Set(arr);
      return uniqueValues.size !== arr.length;
    },
  }

  public static readonly string = {

    parseString: (value?: any | null) => {
      return String(value);
    },

    filterLike: (sourceStr?: string | null, findingStr?: string | null): boolean => {
      return Helper.string.trimLowerCase(sourceStr).includes(Helper.string.trimLowerCase(findingStr));
    },

    upperCaseFirstL: (raw: string) => {
      const first = raw[0];
      const rest = raw.substring(1, raw.length);
      return `${first.toUpperCase()}${rest}`;
    },

    formatId: {
      toNumber: (formattedId?: string | null | number): number => {
        if (!formattedId) return -1;
        if (typeof formattedId === "number") return formattedId;
        const [_, id] = formattedId.split('-');
        if (!id) return Number(_);
        return Number(id);
      },
      toId: (id: string | number | null | undefined, start: string, lenght: number): string => {
        if (!id) return '';
        return `${start}-${String(id).padStart(lenght, '0')}`;
      }
    },

    aplhapbet: {
      lowerCase: "abcdefghijklmnopqrstuvwxyz",
      upperCase: "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
      numbers: "0123456789",
    },

    hasCommonCharacter: (str1: string, str2: string) => {
      const set1 = new Set(str1);
      const set2 = new Set(str2);
      return [...set1].some(char => set2.has(char));
    },

    trimLowerCase: (value: string | undefined | null) => value ? value.toLowerCase().trim() : '',

    generateRandom: (length: number, word: boolean = false) => {
      const alphabet = `${Helper.string.aplhapbet.lowerCase}${Helper.string.aplhapbet.upperCase}${Helper.string.aplhapbet.numbers}`;
      let rand = '';
      for (let i = 0; i < length;) {
        const space = Helper.number.generateRandom(1, 100) <= 25;
        rand += space ? ' ' : alphabet[Helper.number.generateRandom(0, alphabet.length - 1)];
        if (word && space) i++;
        if (!word) i++;
      }
      return rand;
    },

    replaceAll: (str: string, wordToReplace: string, replacementWord: string) => {
      const escapedWord = wordToReplace.replace(/([.*+?^=!:${}()|[\]\\])/g, '\\$1');
      const regex = new RegExp(`\\b${escapedWord}\\b`, 'g');
      return str.replace(regex, replacementWord);
    },

    extractStringInParentheses: (input?: string | null): string | null => {
      if (!input) return '';
      const regex = /\(([^)]+)\)/;
      const match = input.match(regex);
      return match ? match[1] : null;
    },

    bindToStringTemplate: (template: string, key_value: Record<string, string>) => {
      Object.entries(key_value).forEach(([k, v]) => template = template.replace(`{{${k}}}`, v));
      return template;
    },

    toTitleCase: (value: string): string => {
      return value.split(" ").map(v => `${v.slice(0, 1).toUpperCase()}${v.substring(1, v.length)}`).reduce((c, n) => `${c} ${n}`, "");
    },

    reverse: (value: string): string => {
      return value.split('').reverse().join('');
    }
  }

  public static readonly number = {
    currency: (inital: number | string | null | undefined, currency: string): string => {
      if (!inital) return '';
      if (typeof inital == "string") inital = Number(inital);
      const raw = inital.toFixed(2);
      const [wholeNumber, decimal] = String(raw).split(".");
      const proper = Helper.string.reverse(Helper.string.reverse(wholeNumber).split('').map((v, i) => i == 0 ? v : (i % 3 == 0 ? `,${v}` : v)).join(''));
      return `${currency} ${proper}.${decimal}`;
    },

    generateRandom: (min: number, max: number) => {
      return Math.floor(Math.random() * (max - min + 1)) + min;
    },

    numberToWords: (rawNum: number, decimalLimit: number = 2) => {
      if (isNaN(rawNum)) return "Invalid number";

      const num = Number(rawNum.toFixed(decimalLimit));
      if (isNaN(num)) return "Invalid number";

      if (decimalLimit > 4) return "Decimal limit max is 4.";

      const belowTwenty = [
        "", "One", "Two", "Three", "Four", "Five", "Six", "Seven",
        "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen",
        "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"
      ];

      const tens = [
        "", "", "Twenty", "Thirty", "Forty", "Fifty",
        "Sixty", "Seventy", "Eighty", "Ninety"
      ];

      const scales = ["", "Thousand", "Million", "Billion", "Trillion"];

      function convertChunk(n: number): string {
        let str = "";

        if (n >= 100) {
          str += belowTwenty[Math.floor(n / 100)] + " Hundred";
          n %= 100;
          if (n > 0) str += " ";
        }

        if (n >= 20) {
          str += tens[Math.floor(n / 10)];
          n %= 10;
          if (n > 0) str += " " + belowTwenty[n];
        } else if (n > 0) {
          str += belowTwenty[n];
        }

        return str.trim();
      }

      function convertWholeNumber(n: number): string {
        if (n === 0) return "Zero";

        let str = "";
        let scaleIndex = 0;

        while (n > 0) {
          const chunk = n % 1000;
          if (chunk > 0) {
            const chunkStr = convertChunk(chunk);
            str =
              chunkStr +
              (scales[scaleIndex] ? " " + scales[scaleIndex] : "") +
              (str ? " " + str : "");
          }
          n = Math.floor(n / 1000);
          scaleIndex++;
        }

        return str.trim();
      }

      const [wholePartStr, decimalPartStr] = num.toString().split(".");
      const wholePart = parseInt(wholePartStr, 10);

      // Always format decimal to fixed length (e.g., 2 → 00, 50, 75)
      let decimalFormatted = "0".repeat(decimalLimit);

      if (decimalPartStr) {
        decimalFormatted = decimalPartStr
          .padEnd(decimalLimit, "0")
          .slice(0, decimalLimit);
      }

      const result = convertWholeNumber(wholePart);
      const decimalPart = (`${decimalFormatted}/${"1" + "0".repeat(decimalLimit)}`).replace("00/100", "XX/100");
      return `${result} and ${decimalPart}`;
    }
  }

  public static readonly object = {

    /**
     * Transform object into array of object with structure of `{ key, value }`.
     * @param object object to tranform
     * @returns `{ key, value }[]`
     */
    toArray: <T>(object: Object) => {
      const pairs: { key: string, value: T }[] = [];
      Object.entries(object).forEach(([key, value]) => pairs.push({ key, value }));
      return pairs;
    }
  }

  public static readonly date = {
    compareDate: (
      firstDate?: string | Date | null,
      compareOprator?: '=' | '>=' | '<=' | '>' | '<',
      secondDate?: string | Date | null
    ) => {
      if (!firstDate || Helper.date.invalidDate(firstDate)) return false;
      if (!secondDate || Helper.date.invalidDate(secondDate)) return false;
      const first = new Date(firstDate).getTime();
      const second = new Date(secondDate).getTime();

      switch (compareOprator) {
        case '=': return first == second;
        case '>=': return first >= second;
        case '<=': return first <= second;
        case '>': return first > second;
        case '<': return first < second;
        default: return false;
      }
    },

    /**
     * Returns `True` if Date is invalid. 
     * @param checkDate 
     */
    invalidDate: (checkDate: unknown): boolean => {
      const value = String(checkDate);
      if (value === "Invalid Date") return true;
      if (checkDate instanceof Date && String(new Date(checkDate)) === "Invalid Date") return true;
      return false;
    },

    newDate: new Date(),

    getNumberDays: (month: number, year: number): number => {
      // Check for invalid month
      if (month < 1 || month > 12) {
        return -1;
      }

      // CHeck if leap year
      const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

      // Set the number of days based on month and leap year status
      const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
      if (month === 2 && isLeapYear) {
        return 29; // February has 29 days in a leap year
      } else {
        return daysInMonth[month - 1];
      }
    },

    /**
     * Perform addition/subtraction to day of Date 
     * @param input the base date
     * @param dayToAdd number of days to add (can be positive or negative)
     */
    addDay: (input?: Date | string | null, dayToAdd: number = 10) => {
      if (!input) return new Date();
      const newDate = new Date(input);
      newDate.setDate(newDate.getDate() + dayToAdd);
      return newDate;
    },

    /**
     * Format a date 
     * @param input the Date to format
     * @param type `1`-> yyyy-mm-dd `2`-> dd-mm-yyy `3`-> mm-dd-yyy | `Add 0 to add "Time" (ex: 10, 20, 30)` -> `y-m-d h:m:s`
     * @param separator string that seperate values
     */
    format: (input: Date | string | null | undefined, type: number, seperator: string) => {
      if (!input) return '';
      const [y, m, d, _, hour, min, sec] = Helper.date.dateDestruct(new Date(input));
      const [year, month, day] = [String(y).padStart(4, '0'), String(m).padStart(2, '0'), String(d).padStart(2, '0')];

      if (type == 1) return `${year}${seperator}${month}${seperator}${day}`;
      if (type == 2) return `${day}${seperator}${month}${seperator}${year}`;
      if (type == 3) return `${month}${seperator}${day}${seperator}${year}`;

      if (type == 10) return `${year}${seperator}${month}${seperator}${day} ${hour}:${min}:${sec}`;
      if (type == 20) return `${day}${seperator}${month}${seperator}${year} ${hour}:${min}:${sec}`;
      if (type == 30) return `${month}${seperator}${day}${seperator}${year} ${hour}:${min}:${sec}`;
      return `${year}-${month}-${day}`;
    },

    /**
     * Desctuct a Date to Array of values in numbers
     * @param value 
     * @returns [ year, month, date, day, hour, min, sec, mili ]
     */
    dateDestruct: (value?: string | Date | null) => {
      if (!value) return [];
      const date = new Date(value);
      return [date.getFullYear(), date.getMonth() + 1, date.getDate(), date.getDay(), date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds()];
    },

    improperToProperformat: (improperDateFromLogs?: string | null) => {
      if (!improperDateFromLogs) return '';

      // The format passed from auditlogs is YYYY-MM-DD HH:MM:SS PM Monday";
      // Get all the necessary data
      const [date, time, m, day] = improperDateFromLogs.split(' ');

      return new Date(`${date} ${time} ${m}`).toString();
    },

    monthList: {
      short: ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"],
      long: ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"],
    }


  }

  /**
   * Only use this in (keypress) event
   */
  public static readonly input = {

    /**
     * Only accepts Aplhabets, Number (WITHOUT SPACE)
     */
    alphaNumeric: (e: KeyboardEvent) => {
      const char = e.key;
      const regex = /[a-zA-Z0-9-+]/;
      if (!regex.test(char)) {
        e.preventDefault();
      }
    },

    /**
     * Only accepts Aplhabets, Number
     */
    alphaNumericSpace: (e: KeyboardEvent) => {
      const char = e.key;
      const regex = /[a-zA-Z0-9-+ ]/;
      if (!regex.test(char)) {
        e.preventDefault();
      }
    },

    /**
     * Only accepts Aplhabets (WITHOUT SPACE)
     */
    aplha: (e: KeyboardEvent) => {
      const char = e.key;
      const regex = /[a-zA-Z]/;
      if (!regex.test(char)) {
        e.preventDefault();
      }
    },

    /**
     * Only accepts Aplhabets
     */
    alphaSpace: (e: KeyboardEvent) => {
      const char = e.key;
      const regex = /[a-zA-Z ]/;
      if (!regex.test(char)) {
        e.preventDefault();
      }
    }
    ,
    /**
     * Only accepts Numbers
     */
    numericSpace: (e: KeyboardEvent) => {
      const inputElement = (e.target as HTMLInputElement);
      inputElement.value.replace(/[^0-9]/g, "");

      const char = e.key;
      const regex = /[0-9-+. ]/;
      if (!regex.test(char)) {
        e.preventDefault();
      }
    },

    /**
     * Only accepts Numbers (WITHOUT SPACE)
     */
    positiveNumeric: (e: KeyboardEvent) => {
      const inputElement = (e.target as HTMLInputElement);
      inputElement.value.replace(/[^0-9]/g, "");

      const char = e.key;
      const regex = /[0-9.]/;
      if (!regex.test(char)) {
        e.preventDefault();
      }
    },

    /**
     * Only accepts Numbers (WITHOUT SPACE)
     */
    numeric: (e: KeyboardEvent) => {
      const inputElement = (e.target as HTMLInputElement);
      inputElement.value.replace(/[^0-9]/g, "");

      const char = e.key;
      const regex = /[0-9-+.]/;
      if (!regex.test(char)) {
        e.preventDefault();
      }
    },
  }


  /**
   * Return the number inside parentheses `ex. "somtehing(123)else" => 123`.
   * 
   * Return `-1` if nothing found.
   */
  public static extractNumbersInParentheses(input?: string | null): number {
    if (!input) return -1;

    const match = input.match(/\((\d+)\)/);
    if (match && match[1]) return parseInt(match[1], 10);

    return -1;
  }

  /**
   * Handle API errors
   * @param err 
   * @param prompt 
   */
  public static handleError(err: any, prompt?: PromptMessage | null) {
    const getErrorMessage = (error: any) => {
      if (typeof (error) == typeof ('')) return error;
      if (error.errors) return Helper.string.replaceAll((error.errors as string[]).toString(), ',', ', ');
      if (error.message) return error.message;
      return 'Server error.';
    }

    if (!Helper.env.production) console.warn('Error occured: ', err);

    const error = (err as HttpErrorResponse).error;
    const errorMessage = getErrorMessage(error);
    prompt?.set(Helper.errorMessage.apiErrorMessage(errorMessage), 'alert alert-danger mb-4');
    return Helper.errorMessage.apiErrorMessage(errorMessage);
  }


  /**
   * Mark controls as untouched in a FormGroup.
   * @param FormGroup 
   */
  public static markAllAsUntouched(formGroup: FormGroup) {
    for (const controlKey in formGroup.controls) {
      const control = formGroup.controls[controlKey];
      if (control instanceof FormArray) for (const formGroupArray of control.controls as FormGroup[]) Helper.markAllAsUntouched(formGroupArray);
      else control.markAsUntouched();
    }
  }

  /**
   * Mark as touched all invalid controls in a FormGroup.
   * @param FormGroup 
   */
  public static getInvalidControls(formGroup?: FormGroup | null, debugMode = false) {
    if (!formGroup) return [];
    const errors: InvalidControlInreface[] = [];

    const markInvalidControl = (control: FormControl, key: string) => {
      for (const errKey in control?.errors) {
        control.markAllAsTouched();
        errors.push({ control, key, errKey });
        if (debugMode) console.warn({ invalidControl: { control, key } });
      }
    };

    const validateFormArray = (formArray: FormArray) => {
      for (const aControlKey in formArray.controls) {
        const aControl = formArray.controls[aControlKey];

        if (aControl instanceof FormControl) markInvalidControl(aControl, aControlKey);
        if (aControl instanceof FormGroup) Helper.getInvalidControls(aControl, debugMode).forEach(v => errors.push(v));
        if (aControl instanceof FormArray) validateFormArray(aControl);
      }
    }

    for (const cKey in formGroup.controls) {
      const aControl = formGroup.controls[cKey];

      if (aControl instanceof FormControl) markInvalidControl(aControl, cKey);
      if (aControl instanceof FormGroup) Helper.getInvalidControls(aControl, debugMode).forEach(v => errors.push(v));
      if (aControl instanceof FormArray) validateFormArray(aControl);
    }

    return errors;
  }

  public static readonly reactiveForms = {
    control: <T>(value?: T | null, validators?: ValidatorFn[], asyncValidators?: AsyncValidatorFn[]) => {
      return new FormControl<T | undefined | null>(value, { validators: validators ?? [], asyncValidators: asyncValidators ?? [] })
    },
    array: <T extends AbstractControl>(value: T[]) => {
      return new FormArray<T>(value);
    },
    group: <T extends AbstractControl>(value: { [K in keyof T]: AbstractControl<any, any>; }) => {
      return new FormGroup<{ [K in keyof T]: AbstractControl<any, any>; }>(value);
    },
    validatos: Validators,
    customValidator: CustomValidator,
  }

  public static readonly fixedValues = {
    // Regions: [
    //   "BARMM",
    //   "CAR", "NCR",
    //   "Region I", "Region II",
    //   "Region III", "Region IV-A",
    //   "Region IV-B", "Region IX",
    //   "Region V", "Region VI",
    //   "Region VII", "Region VIII",
    //   "Region X", "Region XI",
    //   "Region XII", "Region XIII",
    // ],
    PHPBankNames: [
      "Banco de Oro (BDO Unibank)",
      "Metropolitan Bank and Trust Company (Metrobank)",
      "Bank of the Philippine Islands (BPI)",
      "Philippine National Bank (PNB)",
      "China Banking Corporation (China Bank)",
      "Land Bank of the Philippines (LBP)",
      "Security Bank (SB)",
      "Rizal Commercial Banking Corporation (RCBC)",
      "UnionBank of the Philippines (UnionBank)",
    ],
  }

  public static readonly chartOptions = {
    pie: {
      series: [45, 30, 25],
      chart: {
        width: 380,
        type: "donut"
      },
      labels: ["Income", "Expense", "Balance"],
      dataLabels: {
        enabled: false
      },
      fill: {
        type: "gradient"
      },
      title: {
        text: "Balance report",
        align: "left",
      },
      colors: ["#6F4EF6", "#5BC5A8", "#FF9720"],
      legend: {
        formatter: function (val: string, opts: { w: { globals: { series: { [x: string]: string; }; }; }; seriesIndex: string | number; }) {
          return val + " - " + opts.w.globals.series[opts.seriesIndex];
        }
      },
      responsive: [
        {
          breakpoint: 1600,
          options: {
            chart: {
              width: 315
            }
          }
        },
        {
          breakpoint: 1200,
          options: {
            chart: {
              width: 400
            }
          }
        },
        {
          breakpoint: 575,
          options: {
            chart: {
              width: 300,
              height: 250
            },
            legend: {
              position: "bottom"
            }
          }
        }
      ]
    } as PieChartOptions,
    bar: {
      series: [
        {
          name: "Expense",
          data: [20, 45, 85, 100, 120, 160]
        },
        {
          name: "Income",
          data: [56, 80, 95, 125, 150, 190]
        }
      ],
      chart: {
        type: "bar",
        height: 500,
        width: '100%',
      },
      dataLabels: {
        enabled: false
      },
      fill: {
        opacity: 1
      },
      stroke: {
        show: true,
        width: 2,
        colors: ["transparent"]
      },
      legend: {
        position: "top",
        horizontalAlign: "right",
        floating: true,
        offsetY: -25,
        offsetX: -5
      },
      title: {
        text: "Cashflow report",
        align: "left",
      },
      colors: ["#f5e4e4", "#f64e4e"],
      tooltip: {
        y: {
          formatter: function (val: number) {
            return `₱  ${val}`;
          }
        }
      },
      xaxis: {
        categories: [
          "2016",
          "2017",
          "2018",
          "2019",
          "2020",
          "2021",
        ]
      },
      yaxis: {
        min: 10,
        max: 200,
        title: {
          text: "Income Report"
        }
      },
    } as BarChartOptions,
    spline: {
      series: [
        {
          name: "Expense",
          data: [15, 65, 85, 45, 100, 75, 100, 55, 120, 140, 100, 165]
        },
        {
          name: "Income",
          data: [35, 95, 45, 32, 85, 120, 41, 140, 160, 100, 180, 195]
        }
      ],
      chart: {
        height: 445,
        width: '100%',
        type: "area",
      },
      dataLabels: {
        enabled: true
      },
      title: {
        text: "General ledger report",
        align: "left",
      },
      colors: ["#E95582", "#6F4EF6"],
      stroke: {
        curve: "smooth"
      },
      xaxis: {
        categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
      },
      yaxis: {
        min: 10,
        max: 200
      },
      legend: {
        position: "top",
        horizontalAlign: "right",
        floating: true,
        offsetY: -25,
        offsetX: -5
      },
      tooltip: {
        y: {
          formatter: function (val: number) {
            return `₱  ${val}`;
          }
        }
      },
    } as SplineChartOptions,
  }

  private constructor() { }
}